// ═══════════════════════════════════════════════════════════
// QRPanel — Renders an SVG QR code for a deep link
// Lazy-loads the qrcode library on first render (~50 KB savings)
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from "react";

interface Props {
  value: string;
  size?: number;
  label?: string;
  sublabel?: string;
}

export default function QRPanel({ value, size = 140, label, sublabel }: Props) {
  const [svg, setSvg] = useState<string>("");

  useEffect(() => {
    if (!value) return;
    let cancelled = false;
    import("qrcode").then(({ default: QRCode }) => {
      if (cancelled) return;
      QRCode.toString(value, {
        type: "svg",
        margin: 1,
        width: size,
        color: { dark: "#1A2747", light: "#FFFFFF" },
        errorCorrectionLevel: "M",
      })
        .then((s) => { if (!cancelled) setSvg(s); })
        .catch(() => { if (!cancelled) setSvg(""); });
    });
    return () => { cancelled = true; };
  }, [value, size]);

  return (
    <div className="flex items-center gap-4 p-4">
      <div
        className="rounded-xl bg-white p-2 border border-[rgba(15,23,42,0.06)] flex-shrink-0 flex items-center justify-center"
        style={{ width: size + 16, height: size + 16 }}
        aria-label="QR code"
      >
        {svg ? <span dangerouslySetInnerHTML={{ __html: svg }} /> : null}
      </div>
      <div className="flex-1 min-w-0">
        {label && <div className="text-[12px] font-semibold text-[#1A2747] leading-snug mb-1">{label}</div>}
        {sublabel && <div className="text-[10px] text-[#6B7280] leading-snug">{sublabel}</div>}
      </div>
    </div>
  );
}
